import React from 'react';
import tw from 'twin.macro';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import { Color } from '@teetsh/app/src/types/colors';
import HtmlViewer from '@teetsh/app/src/components/HtmlViewer';
import AttachmentLink from '@teetsh/app/src/components/Attachments/AttachmentLink';
import {
  DocumentTextIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Attachment } from '@teetsh/app/src/types/attachment';
import { FicheDePrep, Sequence } from '@teetsh/app/src/types/sequences';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { Tag } from '@teetsh/app/src/components/Tag';
import { isElementEmpty } from '../../isElementEmpty';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

interface Props {
  value: string;
  color?: Color;
  fill?: boolean;
  attachments?: Attachment[];
  sequence?: Pick<Sequence, 'id' | 'title'>;
  ficheDePrep?: Pick<FicheDePrep, 'id' | 'title' | 'sequenceId'>;
  tag?: { label: string; color?: Color | TagColorWithIntensity };
}

export function Element(props: Props) {
  const {
    value,
    color = 'white',
    fill,
    attachments,
    sequence,
    ficheDePrep,
    tag,
  } = props;
  const { t } = useTranslation(['programmation']);
  const { id: userId } = useUser();

  if (isElementEmpty(props)) {
    return null;
  }

  // we don't want to break small elements over 2 pages
  // so here is a rule of thumb : if there are less than 3 paragraphs tags, we don't break
  const avoidBreak =
    (value.match(/<p/g) || []).length <= 3 && !value.includes('<img');

  return (
    <div
      css={[
        tw`p-2 rounded-lg relative flex gap-2 flex-col flex-wrap border border-neutral-quiet text-sm min-w-0`,
        { p: tw`[overflow-wrap:anywhere]` }, // see https://github.com/tailwindlabs/tailwindcss/discussions/2213
        fill && tw`flex-grow`,
        bgColors(color),
        getTextColorFromBackgroundColor(color),
        avoidBreak && tw`print:break-inside-avoid`,
      ]}
    >
      <HtmlViewer
        onChange={() => {}}
        text={value.replaceAll('tr=w-1200', 'tr=w-400')}
        print
      />
      {ficheDePrep?.id && (
        <div tw="flex items-center gap-x-1.5 font-semibold min-w-0">
          <DocumentTextIcon tw="shrink-0 w-5 h-5" />
          <Link
            target="_blank"
            tw="hover:underline text-neutral-intense flex-1"
            to={`/users/${userId}/sequences/${ficheDePrep?.sequenceId}/fiches-de-prep/${ficheDePrep?.id}/print`}
          >
            {ficheDePrep?.title || t('programmation:noTitleFdp')}
          </Link>
        </div>
      )}
      {sequence?.id && (
        <div tw="flex items-center gap-x-1.5 font-semibold min-w-0">
          <RectangleStackIcon tw="shrink-0 w-5 h-5" />
          <Link
            target="_blank"
            tw="hover:underline text-neutral-intense flex-1"
            to={`/users/${userId}/sequences/${sequence.id}/print`}
          >
            {sequence?.title || t('programmation:noTitleSequence')}
          </Link>
        </div>
      )}
      {(attachments || []).length > 0 && (
        <div tw="font-semibold">
          {(attachments || []).map(a => (
            <AttachmentLink
              noColor
              largeIcon
              key={a.label}
              attachment={a}
              preview
            />
          ))}
        </div>
      )}
      {tag && (
        <Tag tw="mt-1" outlined color={tag.color}>
          {tag.label}
        </Tag>
      )}
    </div>
  );
}
